import { money, AOTC_ELIGIBLE_CATEGORIES } from "@/lib/format";
import { isUSD } from "@/lib/currencies";

// ── THE TAX-CREDIT SET-ASIDE IS A THIRD ANSWER TO "Use the 529 for this
// expense?" (ZEN-833; design approved by Maryse, 30 Oct 2026, lane Oct30 ZEN-164:
// "I approve the design, let's go with that"). Rule: part credit, part 529 on one
// expense, never the same dollar (her ZEN-164 ruling, 30 Oct 2026).
// The question and its three answers, asked the same way wherever an expense
// is classified. Approved by Maryse, 31 Oct 2026, in the ZEN-833 lane session
// ("I approve the new wording here"), in this order: Yes, No, Partly; the No
// answer in the same shape as the other two ("agree with the same shape"). She had
// noticed "Draw from the 529 account?" does not fit an answer that reserves
// part of the expense for the AOTC. Before: "Draw from the 529 for this
// expense?" and "Yes — reserve for the AOTC first, …".
export const DRAW_QUESTION = "Use the 529 for this expense?";
export const DRAW_YES_LABEL = "Yes — use 529 funds";
export const DRAW_NO_LABEL = "No — don't use 529 funds";
export const RESERVE_ANSWER_LABEL = "Partly — reserve for the AOTC first, use 529 funds for the rest";
// The error when the question is left unanswered. Approved by Maryse, 31 Oct 2026.
export const DRAW_QUESTION_UNANSWERED = "Say whether to use the 529 for this expense";

// "Reserved" read exactly as the server reads it (expense_logic._reserved_cents:
// any yes-like stored value), so the two can never disagree (ZEN-164 round 5).
export const holdsReservation = (e) => !!(e && e.aotc_reserved);

// The screen's reading of expense_logic.figure_is_final (ZEN-834): a US dollar
// cost, a rate entered by hand, or a published rate marked final. A rate still
// loading or not found is not final.
export const rateIsFinal = ({ currency, handRate, fx }) =>
  isUSD(currency) || !!handRate || (!!fx && fx.final === true);

// When the third answer is on the list. An expense already holding a
// reservation always shows it, so money set aside is never hidden.
export function reserveAnswerOffered({ held, cardSaysYes, qualifies, room, rateFinal }) {
  if (held) return true;
  return !!cardSaysYes && !!qualifies && room > 0.005 && !!rateFinal;
}

// The smaller of the cost and the room left. While the rate is an estimate a
// stored figure may stay or shrink but never grow (ZEN-834, her 31 Oct answer).
export function reserveFigures(usd, room, { cap = Infinity } = {}) {
  const reserved = Math.max(0, Math.round(Math.min(usd, room, cap) * 100) / 100);
  const rest = Math.max(0, Math.round((usd - reserved) * 100) / 100);
  return { reserved, rest };
}

// The one line under the question. Examples approved on the ticket.
export const reserveLine = ({ reserved, rest }) =>
  `${money(reserved, "USD")} reserved for the AOTC · ` +
  (rest > 0.005 ? `the 529 can pay ${money(rest, "USD")}` : "nothing left for the 529 to pay");

// Shown in place of the third answer while the rate is an estimate. The one
// copy. Approved by Maryse, 31 Oct 2026, in the ZEN-833 lane session ("approve
// all new shorter wording").
export const RESERVE_WAITS_FOR_RATE =
  "You can reserve this expense for the AOTC once the official exchange rate is in, or now if you enter the rate by hand.";

// The error when "Partly" is saved on a computer whose course-of-study box is
// not ticked (ZEN-833 round 1). Approved by Maryse, 3 Nov 2026, in the ZEN-833
// lane session: "Approve as written". Shaped like the scholarship one beside it.
export const COMPUTER_UNCONFIRMED =
  "Confirm the computer or technology was needed for a course of study before saving an AOTC-reserved expense";

// Does the credit reach this cost at all? A college cost (post-secondary) in a
// category the AOTC covers. The one copy of this reading on the screens
// (rule 21). A computer also needs its course-of-study tick, which the expense
// window asks next to the answer list.
export const creditCovers = ({ group, category }) =>
  group === "post_secondary" && AOTC_ELIGIBLE_CATEGORIES.has(category);
